import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetCaseRateRulesetByIdQuery } from "store/queries/pricing";

import { BilateralToggle } from "./components/BilateralToggle";
import { CaseRateConflictBanner } from "./components/CaseRateConflictBanner";
import { ContractLanguageField } from "./components/ContractLanguageField";
import { CptCodes } from "./components/CptCodes";
import { ExcludedCptCodes } from "./components/ExcludedCptCodes";
import { Header } from "./components/Header";
import { PerDiemToggle } from "./components/PerDiemToggle";
import { ProviderAndPayerCopySection } from "./components/ProviderAndPayerCopySection";
import { RuleName } from "./components/RuleName";
import { SubmitCaseRate } from "./components/SubmitCaseRate";
import { copyFormScope } from "./helpers/ruleApplicability";

export function CopyCaseRateRulesPage() {
  useDocumentTitle("Copy Case Rate Rules");
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const asDefault = searchParams.get("asDefault") === "true";
  const { data: ruleset, isLoading } = useGetCaseRateRulesetByIdQuery(id);

  const methods = useForm({
    defaultValues: {
      // CLE-2842 step 2 — applicability, the source's provider and payer (All payers on a
      // Payer-Provider rule that had none), and a Fallback's payer.
      ...copyFormScope(ruleset?.data, asDefault),
      groupName: ruleset?.data.groupName,
      cptCodes: ruleset?.data.cptCodes,
      excludedCptCodes: ruleset?.data.excludedCptCodes,
      isPerDiem: ruleset?.data.isPerDiem ?? false,
      isBilateral: ruleset?.data.isBilateral ?? false,
      contractLanguage: ruleset?.data.contractLanguage ?? "",
    },
  });

  useEffect(() => {
    if (ruleset) {
      methods.reset({
        ...copyFormScope(ruleset?.data, asDefault),
        groupName: ruleset?.data.groupName,
        cptCodes: ruleset?.data.cptCodes,
        excludedCptCodes: ruleset?.data.excludedCptCodes,
        isPerDiem: ruleset?.data.isPerDiem ?? false,
        isBilateral: ruleset?.data.isBilateral ?? false,
      });
    }
  }, [ruleset, methods, asDefault]);

  const copyScope = copyFormScope(ruleset?.data, asDefault);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <FormProvider {...methods}>
      <Header name={ruleset?.data.groupName} isCaseRate />
      <ProviderAndPayerCopySection
        initialProviders={copyScope.provider}
        initialPayers={copyScope.payerName}
      />
      <RuleName />
      <div className="grid">
        <PerDiemToggle />
        <BilateralToggle />
      </div>
      <CptCodes />
      <ExcludedCptCodes />
      <ContractLanguageField ruleType="CASE_RATE" />
      <CaseRateConflictBanner />
      {id && ruleset && <SubmitCaseRate />}
    </FormProvider>
  );
}
